import { isServer } from 'lit'
import en from './en-us.js'

// gets list of properties beginning prefix
type PickStartsWith<T, Prefix extends string> = T extends `${Prefix}${string}` ? T : never

// Recursively make all properties optional
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

export type Translation = typeof en
export type PartialTranslation = {
  $lang: string
  $name?: string
  $dir?: 'ltr' | 'rtl'
} & DeepPartial<Omit<Translation, '$lang' | '$name' | '$dir'>>
export type WellKnownKeys = PickStartsWith<keyof Translation, '$'>

const documentLangSubscribers = new Set<() => void>()
const translationRegistrationSubscribers = new Set<() => void>()
const translations = new Map<string, Translation>()

function notifyDocumentLangChange() {
  for (const subscriber of documentLangSubscribers) {
    subscriber()
  }
}

function notifyTranslationRegistration() {
  for (const subscriber of translationRegistrationSubscribers) {
    subscriber()
  }
}

function deepMerge(target: unknown, source: unknown): unknown {
  if (!isObject(source))
    return source

  if (!isObject(target))
    return source

  const result = { ...target }

  for (const key in source) {
    const sourceValue = (source as Record<string, unknown>)[key]
    const targetValue = result[key]

    if (isObject(sourceValue) && isObject(targetValue)) {
      result[key] = deepMerge(targetValue, sourceValue)
    }
    else {
      result[key] = sourceValue
    }
  }

  return result
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

// observe changes to the document's lang
if (!isServer) {
  const observer = new MutationObserver(notifyDocumentLangChange)

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang'],
  })
}

/**
 * Removes all registered translations
 */
export function clearTranslations() {
  translations.clear()
}

/**
 * Registers one or more translations. If a translation for the same language
 * already exists, the new translation will be deep-merged with the existing one.
 * Translations can be partial — only the fields you provide will override existing values.
 */
export function registerTranslation(...translation: (Translation | PartialTranslation)[]) {
  translation.forEach((t) => {
    const lang = t.$lang.toLowerCase()
    const existing = translations.get(lang)

    // Deep merge: keep existing keys, override with new ones
    const merged = existing ? (deepMerge(existing, t) as Translation) : (t as Translation)
    translations.set(lang, merged)
  })

  notifyTranslationRegistration()
}

/**
 * subscribe to document language changes
 * @param onChange callback for when document `lang` attr changes
 * @returns cleanup function
 */
export function subscribeToDocumentLang(onChange: () => void): () => void {
  documentLangSubscribers.add(onChange)
  return () => documentLangSubscribers.delete(onChange)
}

/**
 * subscribe to translation registration changes
 * @param onChange callback for when new translations are registered
 * @returns cleanup function
 */
export function subscribeToTranslationRegistration(onChange: () => void): () => void {
  translationRegistrationSubscribers.add(onChange)
  return () => translationRegistrationSubscribers.delete(onChange)
}

/**
 * Check whether there is a translation registered for the given lang
 * @param lang the lang code e.g. "en" or "en-GB"
 */
export function isTranslationRegistered(lang: string) {
  return translations.has(lang)
}

/**
 * Picks the most appropriate translation for the given language, from most specific to least specific.
 * First tries lang + region, then lang only, then fallback.
 */
export function resolveTranslation(langCode: string) {
  const lang = langCode.toLowerCase()
  const [langOnly] = lang.split('-')

  return translations.get(lang) || translations.get(langOnly) || en
}

export { en as fallback }
